'use client';

import { ReactNode } from 'react';
import { css, stack } from '@bts-web/utils-style-engine';
import { ButtonBase, DialogBase, HyperLinkButtonBase } from '../../../common';
import { ModalComponent } from '../ModalDrawerContext/ModalDrawerContext';
import { RegularInfoWarning } from '../../svgIcons/RegularInfoWarning';

export type WarningModalProps = {
  title: string;
  subtitle?: string;
  description?: string | ReactNode;
  highlightedDescription?: string | ReactNode;
  continueLink?: {
    text: string;
    url: string;
  };
  cancelBtnText: string;
  enableClose?: boolean;
};

const containerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  mx: 'auto',
  gap: 'medium',
  pb: 'medium',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  minHeight: '240px',
});

export const WarningModal: ModalComponent<WarningModalProps> = ({
  title,
  subtitle,
  description = '',
  highlightedDescription,
  onClose,
  continueLink,
  cancelBtnText,
  enableClose,
}) => {
  return (
    <DialogBase
      id="transaction-warning-modal"
      onClose={onClose}
      size="small"
      onMobileFullScreen
      visual="secondary"
      disableCloseButton={!!enableClose === false}
    >
      <section className={containerStyles}>
        <div
          className={css({ display: 'flex', justifyContent: 'center' })}
          aria-label="warning icon section"
        >
          <div
            className={css({
              width: '52px',
              height: '52px',
              color: 'warning.text_primary',
              backgroundColor: 'warning.fill_secondary',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              flexShrink: 0,
            })}
          >
            <RegularInfoWarning size={36} />
          </div>
        </div>
        {title && (
          <h1
            className={css({
              fontSize: 'headline.medium',
              fontWeight: 'headline.large_semi_bold',
              lineHeight: 1.1,
            })}
          >
            {title}
          </h1>
        )}
        {subtitle && (
          <div
            className={css({
              textAlign: 'center',
              color: 'neutrals.text_primary',
              fontSize: 'headline.small',
              fontWeight: 'headline.small',
              letterSpacing: 'headline.small',
              lineHeight: 'headline.small',
            })}
          >
            {subtitle}
          </div>
        )}

        {description && (
          <div
            className={css({
              color: 'neutrals.text_secondary',
              fontSize: 'body.medium',
              fontWeight: 'body.medium',
              letterSpacing: 'body.medium',
              lineHeight: 'body.medium',
            })}
          >
            {description}
          </div>
        )}

        {highlightedDescription && (
          <div
            className={css({
              color: 'neutrals.text_secondary',
              fontSize: 'body.medium_medium',
              fontWeight: 'body.medium_medium',
              letterSpacing: 'body.medium_medium',
              lineHeight: 'body.medium_medium',
            })}
          >
            {highlightedDescription}
          </div>
        )}
      </section>
      <section
        className={stack({
          marginInlineStart: 'auto',
          marginInlineEnd: 'auto',
          maxWidth: '390px',
          width: '100%',
          gap: 'small',
          alignItems: 'center',
          mt: 'auto',
        })}
      >
        {continueLink?.url && (
          <HyperLinkButtonBase
            onClick={() => {
              if (onClose) {
                onClose();
              }

              document.dispatchEvent(new Event('onWarningModalContinue'));
            }}
            size="large"
            to={continueLink.url}
            visual="primary"
            prefetch
            data-testid="continue button"
          >
            {continueLink.text}
          </HyperLinkButtonBase>
        )}

        <ButtonBase
          fullWidth
          size="large"
          onClick={onClose}
          visual="secondary"
          date-testid="do-it-later-btn"
        >
          {cancelBtnText}
        </ButtonBase>
      </section>
    </DialogBase>
  );
};
